<template>
  <div class="dashboard">
    <!-- 主要内容区 -->
    <main class="max-w-7xl mx-auto px-4">
      <!-- 欢迎信息 -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-800">{{ $store.getters.t('welcome') }}</h1>
        <p class="text-gray-600">{{ $store.getters.t('today', { date: currentDate }) }}</p>
      </div>

      <!-- 数据概览卡片 -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="bg-white rounded-lg shadow-md p-6 card-hover">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-blue-100 text-blue-600">
              <i class="fas fa-users text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-gray-500">{{ $store.getters.t('active_streamers') }}</p>
              <p class="text-2xl font-bold">{{ $store.state.dashboardData.activeStreamers }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow-md p-6 card-hover">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-green-100 text-green-600">
              <i class="fas fa-chart-line text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-gray-500">{{ $store.getters.t('income_today') }}</p>
              <p class="text-2xl font-bold">¥{{ $store.state.dashboardData.incomeToday.toLocaleString() }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow-md p-6 card-hover">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-purple-100 text-purple-600">
              <i class="fas fa-video text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-gray-500">{{ $store.getters.t('live_sessions') }}</p>
              <p class="text-2xl font-bold">{{ $store.state.dashboardData.liveSessions }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow-md p-6 card-hover">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-yellow-100 text-yellow-600">
              <i class="fas fa-heart text-xl"></i>
            </div>
            <div class="ml-4">
              <p class="text-gray-500">{{ $store.getters.t('new_fans') }}</p>
              <p class="text-2xl font-bold">{{ $store.state.dashboardData.newFans.toLocaleString() }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- AI能力专区 -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div class="bg-gradient-to-r from-purple-400 to-indigo-500 rounded-lg shadow-md p-6 card-hover text-white">
          <div class="flex items-center mb-4">
            <i class="fas fa-magic text-3xl"></i>
            <h2 class="text-xl font-bold ml-3">{{ $store.getters.t('ai_recommend') }}</h2>
          </div>
          <p class="mb-4">{{ $store.getters.t('ai_recommend_desc') }}</p>
          <button class="bg-white text-purple-600 px-4 py-2 rounded-lg font-bold hover:bg-gray-100">
            {{ $store.getters.t('view_recommend') }}
          </button>
        </div>
        
        <div class="bg-gradient-to-r from-green-400 to-blue-500 rounded-lg shadow-md p-6 card-hover text-white">
          <div class="flex items-center mb-4">
            <i class="fas fa-calendar-alt text-3xl"></i>
            <h2 class="text-xl font-bold ml-3">{{ $store.getters.t('ai_schedule') }}</h2>
          </div>
          <p class="mb-4">{{ $store.getters.t('ai_schedule_desc') }}</p>
          <button class="bg-white text-green-600 px-4 py-2 rounded-lg font-bold hover:bg-gray-100" @click="goToSchedule">
            {{ $store.getters.t('schedule_now') }}
          </button>
        </div>
        
        <div class="bg-gradient-to-r from-yellow-400 to-pink-500 rounded-lg shadow-md p-6 card-hover text-white">
          <div class="flex items-center mb-4">
            <i class="fas fa-share-square text-3xl"></i>
            <h2 class="text-xl font-bold ml-3">{{ $store.getters.t('one_click_publish') }}</h2>
          </div>
          <p class="mb-4">{{ $store.getters.t('one_click_publish_desc') }}</p>
          <button class="bg-white text-yellow-600 px-4 py-2 rounded-lg font-bold hover:bg-gray-100">
            {{ $store.getters.t('go_publish') }}
          </button>
        </div>
      </div>

      <!-- 功能模块卡片 -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- 主播管理 -->
        <div class="bg-white rounded-lg shadow-md p-6 card-hover relative cursor-pointer" @click="goToStreamers">
          <div class="flex items-center mb-4">
            <i class="fas fa-user-circle text-3xl text-purple-600"></i>
            <h2 class="text-xl font-bold ml-3">{{ $store.getters.t('streamer_manage') }}</h2>
            <span class="ml-2 px-2 py-1 bg-indigo-100 text-indigo-600 text-xs rounded absolute right-4 top-4">{{ $store.getters.t('ai_helper') }}</span>
          </div>
          <p class="text-gray-600 mb-4">{{ $store.getters.t('streamer_manage_desc') }}</p>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-500">{{ $store.state.dashboardData.activeStreamers }}{{ $store.getters.t('streamer_unit') }}</span>
            <i class="fas fa-chevron-right text-gray-400"></i>
          </div>
        </div>

        <!-- 直播管理 -->
        <div class="bg-white rounded-lg shadow-md p-6 card-hover relative cursor-pointer" @click="goToLive">
          <div class="flex items-center mb-4">
            <i class="fas fa-video text-3xl text-blue-600"></i>
            <h2 class="text-xl font-bold ml-3">{{ $store.getters.t('live_manage') }}</h2>
            <span class="ml-2 px-2 py-1 bg-indigo-100 text-indigo-600 text-xs rounded absolute right-4 top-4">{{ $store.getters.t('ai_helper') }}</span>
          </div>
          <p class="text-gray-600 mb-4">{{ $store.getters.t('live_manage_desc') }}</p>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-500">{{ $store.state.dashboardData.liveSessions }}{{ $store.getters.t('live_unit') }}</span>
            <i class="fas fa-chevron-right text-gray-400"></i>
          </div>
        </div>

        <!-- 内容运营 -->
        <div class="bg-white rounded-lg shadow-md p-6 card-hover relative cursor-pointer" @click="goToContent">
          <div class="flex items-center mb-4">
            <i class="fas fa-pencil-alt text-3xl text-green-600"></i>
            <h2 class="text-xl font-bold ml-3">{{ $store.getters.t('content_ops') }}</h2>
            <span class="ml-2 px-2 py-1 bg-indigo-100 text-indigo-600 text-xs rounded absolute right-4 top-4">{{ $store.getters.t('ai_helper') }}</span>
          </div>
          <p class="text-gray-600 mb-4">{{ $store.getters.t('content_ops_desc') }}</p>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-500">12{{ $store.getters.t('activity_unit') }}</span>
            <i class="fas fa-chevron-right text-gray-400"></i>
          </div>
        </div>

        <!-- 财务管理 -->
        <div class="bg-white rounded-lg shadow-md p-6 card-hover relative cursor-pointer" @click="goToFinance">
          <div class="flex items-center mb-4">
            <i class="fas fa-chart-pie text-3xl text-yellow-600"></i>
            <h2 class="text-xl font-bold ml-3">{{ $store.getters.t('finance_manage') }}</h2>
            <span class="ml-2 px-2 py-1 bg-indigo-100 text-indigo-600 text-xs rounded absolute right-4 top-4">{{ $store.getters.t('ai_helper') }}</span>
          </div>
          <p class="text-gray-600 mb-4">{{ $store.getters.t('finance_manage_desc') }}</p>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-500">{{ $store.getters.t('this_month_income') }}：¥{{ $store.state.dashboardData.incomeToday.toLocaleString() }}</span>
            <i class="fas fa-chevron-right text-gray-400"></i>
          </div>
        </div>

        <!-- 智能助手 -->
        <div class="bg-white rounded-lg shadow-md p-6 card-hover relative cursor-pointer" @click="goToAiAssistant">
          <div class="flex items-center mb-4">
            <i class="fas fa-robot text-3xl text-red-600"></i>
            <h2 class="text-xl font-bold ml-3">{{ $store.getters.t('ai_assistant') }}</h2>
            <span class="ml-2 px-2 py-1 bg-indigo-100 text-indigo-600 text-xs rounded absolute right-4 top-4">{{ $store.getters.t('ai_helper') }}</span>
          </div>
          <p class="text-gray-600 mb-4">{{ $store.getters.t('ai_assistant_desc') }}</p>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-500">5{{ $store.getters.t('new_suggestion') }}</span>
            <i class="fas fa-chevron-right text-gray-400"></i>
          </div>
        </div>

        <!-- 数据分析 -->
        <div class="bg-white rounded-lg shadow-md p-6 card-hover relative cursor-pointer" @click="goToAnalytics">
          <div class="flex items-center mb-4">
            <i class="fas fa-chart-bar text-3xl text-indigo-600"></i>
            <h2 class="text-xl font-bold ml-3">{{ $store.getters.t('data_analytics') }}</h2>
            <span class="ml-2 px-2 py-1 bg-indigo-100 text-indigo-600 text-xs rounded absolute right-4 top-4">3</span>
          </div>
          <p class="text-gray-600 mb-4">{{ $store.getters.t('data_analytics_desc') }}</p>
          <div class="flex justify-between items-center">
            <span class="text-sm text-gray-500">{{ $store.getters.t('new_report') }}</span>
            <i class="fas fa-chevron-right text-gray-400"></i>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'DashboardPage',
  computed: {
    currentDate() {
      const now = new Date()
      return now.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      })
    }
  },
  methods: {
    goToStreamers() {
      this.$router.push('/streamers')
    },
    goToLive() {
      this.$router.push('/live')
    },
    goToContent() {
      this.$router.push('/content')
    },
    goToFinance() {
      this.$router.push('/finance')
    },
    goToAiAssistant() {
      this.$router.push('/ai-assistant')
    },
    goToAnalytics() {
      this.$router.push('/analytics')
    },
    goToSchedule() {
      this.$router.push('/schedule')
    }
  }
}
</script>

<style scoped>
.dashboard {
  background: #f5f5f5;
  min-height: calc(100vh - 64px);
}

.card-hover {
  transition: all 0.3s ease;
}

.card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);
}

.grid {
  display: grid;
}

.grid-cols-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}

@media (min-width: 768px) {
  .md\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .lg\:grid-cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  
  .lg\:grid-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

/* 确保网格布局正确工作 */
.grid {
  display: grid !important;
}

.grid-cols-1 {
  grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
}

@media (min-width: 768px) {
  .md\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
  
  .md\:grid-cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  }
}

@media (min-width: 1024px) {
  .lg\:grid-cols-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  }
  
  .lg\:grid-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
  }
}

.gap-6 {
  gap: 1.5rem;
}

.mb-8 {
  margin-bottom: 2rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.ml-4 {
  margin-left: 1rem;
}

.mr-3 {
  margin-right: 0.75rem;
}

.text-2xl {
  font-size: 1.5rem;
}

.text-xl {
  font-size: 1.25rem;
}

.text-lg {
  font-size: 1.125rem;
}

.text-sm {
  font-size: 0.875rem;
}

.font-bold {
  font-weight: 700;
}

.text-gray-500 {
  color: #6b7280;
}

.text-gray-600 {
  color: #4b5563;
}

.text-gray-800 {
  color: #1f2937;
}

.text-blue-600 {
  color: #2563eb;
}

.text-green-600 {
  color: #16a34a;
}

.text-purple-600 {
  color: #9333ea;
}

.text-yellow-600 {
  color: #ca8a04;
}

.text-indigo-600 {
  color: #4f46e5;
}

.text-red-600 {
  color: #dc2626;
}

.text-orange-600 {
  color: #ea580c;
}

.bg-blue-100 {
  background-color: #dbeafe;
}

.bg-green-100 {
  background-color: #dcfce7;
}

.bg-purple-100 {
  background-color: #f3e8ff;
}

.bg-yellow-100 {
  background-color: #fef3c7;
}

.bg-indigo-100 {
  background-color: #e0e7ff;
}

.bg-red-100 {
  background-color: #fee2e2;
}

.bg-orange-100 {
  background-color: #fed7aa;
}

.rounded-full {
  border-radius: 9999px;
}

.p-3 {
  padding: 0.75rem;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.items-start {
  align-items: flex-start;
}

.justify-between {
  justify-content: space-between;
}

.space-x-4 > * + * {
  margin-left: 1rem;
}

.max-w-7xl {
  max-width: 80rem;
}

.mx-auto {
  margin-left: auto;
  margin-right: auto;
}

.px-4 {
  padding-left: 1rem;
  padding-right: 1rem;
}

.py-8 {
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.cursor-pointer {
  cursor: pointer;
}
</style> 